import { EnquiryPayload } from "@/interfaces/propertyinterface";
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import { FilterPayload } from "../redux/slice/propertySlice";
const API_URL = process.env.NEXT_PUBLIC_API_URL;
export function handleAxiosError(
  error: unknown,
  fallbackMessage = "An unknown error occurred",
  axiosFallback = "Failed to fetch data"
): string {
  if (error instanceof AxiosError) {
    const message = error.response?.data?.message;
    // Nest's validation errors arrive as an array; callers test for a
    // string, so an array used to read as success.
    if (Array.isArray(message)) return message.join(". ") || axiosFallback;
    return message || axiosFallback;
  } else {
    return fallbackMessage;
  }
}

export const getAllProperties = async ({
  page = 1,
  filters,
}: {
  page: number;
  filters?: {
    [key: string]: string;
  };
}) => {
  try {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "" && value.length > 0) {
          params.append(key, value);
        }
      });
    }
    const response = await axios.get(
      `${API_URL}/property?page=${page}&${params.toString()}`
    );

    return response.data?.results;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getSingleProperty = async (id: string) => {
  try {
    const response = await axios.get(
      `${API_URL}/property/get-single-property-details/${id}`
    );
    return response.data;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getSinglePropertyByName = async (id: string) => {
  try {
    const response = await axios.get(
      `${API_URL}/property/get-property-by-name/${id}`
    );
    return response.data;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getPropertyByPincodes = async (pincodes: string[]) => {
  try {
    const response = await axios.get(
      `${API_URL}/property?addresses=${pincodes}`
    );
    return response?.data?.results;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getFlatCategoriesByPropertyId = async (propertyId: string) => {
  try {
    const response = await axios.get(
      `${API_URL}/property-meta-data/flats-with-categories/${propertyId}`
    );
    return response?.data;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const postEnquiry = async (data: EnquiryPayload) => {
  try {
    const response = await axios.post(`${API_URL}/enquiry`, data);
    toast.success(response?.data);
    const newResponse = {
      data: response?.data,
      status: response?.status,
    };
    return newResponse;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getRecommendedProperty = async (data: Property | undefined) => {
  try {
    const response = await axios.get(
      `${API_URL}/property/recommended-property?longitude=${
        data?.location?.longitude
      }&latitude=${data?.location?.latitude}&radius=${20}`
    );
    return response.data;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getFilteredProperty = async (data: FilterPayload | undefined) => {
  try {
    const baseUrl = `${API_URL}/property/master-filter`;

    // Construct query parameters dynamically
    const queryParams = new URLSearchParams();
    if (data?.propertyName)
      queryParams.append("property_name", data?.propertyName);
    // if (data?.location) queryParams.append("pin_code", data?.location);
    if (data?.maxPrice) queryParams.append("price_max", data.maxPrice);
    if (data?.minPrice) queryParams.append("price_min", data.minPrice);
    if (data?.top) queryParams.append("top", data.top);
    if (data?.flatType) queryParams.append("type", data.flatType);

    // Handle tenure conditions
    if (data?.Tenure) {
      if (data.Tenure === "Freehold") {
        queryParams.append("is_leasehold", "false");
      } else {
        queryParams.append("is_leasehold", "true");
        queryParams.append("lease_year", data.Tenure);
      }
    }

    // Full URL with dynamic query parameters
    const url = queryParams.toString()
      ? `${baseUrl}?${queryParams.toString()}`
      : baseUrl;

    // API call
    const response = await axios.get(url);
    return response.data.results;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getCompareProperty = async (data: Property[] | undefined) => {
  try {
    const response = await axios.get(
      `${API_URL}/property/compare-property?longitude=${data?.[0]?.location?.longitude}&latitude=${data?.[0]?.location?.latitude}&radius=20`
    );
    return response.data.results;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const sendNotificationOnTelegram = async (data: { message: string }) => {
  try {
    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_CHAT_BOT_API_URL}/send-group-message`,
      { message: data.message }
    );

    return response.data;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};

export const getPriceList = async (id: string | undefined) => {
  try {
    const response = await axios.get(
      `${API_URL}/property-meta-data/export-pricelist/${id}`
    );
    return response.data;
  } catch (error: unknown) {
    return handleAxiosError(error);
  }
};
